import type { RetryOpportunitySetResponse } from "../api/opportunity-sets.api";
import type { BrandVoice, OpportunitySet, StrategicPriorities } from "../types/opportunities.types";

/**
 * Derives the set of IDs currently in a GENERATING state from the server's
 * opportunity-set list. `generatingSetIds.size > 0` drives the 10s poll loop;
 * keeping this derivation pure lets the route stay side-effect free.
 */
export function deriveGeneratingSetIds(sets: readonly OpportunitySet[]): Set<string> {
  const ids = new Set<string>();
  for (const s of sets) {
    if (s.status === "GENERATING") ids.add(s.id);
  }
  return ids;
}

/** Derives the set of IDs whose last generation run failed. */
export function deriveErrorSetIds(sets: readonly OpportunitySet[]): Set<string> {
  const ids = new Set<string>();
  for (const s of sets) {
    if (s.status === "FAILED") ids.add(s.id);
  }
  return ids;
}

/**
 * Projects every server-provided `generationStartedAt` to a unix-ms map so the
 * card can render its live elapsed clock. Rows without a start timestamp (pre-
 * migration legacy rows) are omitted rather than defaulted to `Date.now()` —
 * a bogus start time would lie to the user about how long the job has run.
 */
export function deriveGenerationStartedAt(sets: readonly OpportunitySet[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const s of sets) {
    if (s.generationStartedAt) {
      const t = Date.parse(s.generationStartedAt);
      if (Number.isFinite(t)) out.set(s.id, t);
    }
  }
  return out;
}

/** Maps each FAILED set to its backend error string (if one was captured). */
export function deriveGenerationErrors(sets: readonly OpportunitySet[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const s of sets) {
    if (s.status === "FAILED" && s.generationError) {
      out.set(s.id, s.generationError);
    }
  }
  return out;
}

export interface StatusTransition {
  setId: string;
  name: string;
  from: OpportunitySet["status"];
  to: OpportunitySet["status"];
  generationError: string | null;
  totalCount: number;
}

/**
 * Compares the previous poll's status map to the current sets and returns
 * every set that changed status. New sets (not in `prev`) do not produce a
 * transition — the generating card appears via the optimistic create mutation
 * and its "just completed" pulse fires on the subsequent GENERATING → READY
 * flip, not on initial mount.
 *
 * The caller owns the side effects (toasts, pulse animations). Returning the
 * transitions as data makes the behavior testable without mocking `toast`.
 */
export function detectStatusTransitions(
  prev: ReadonlyMap<string, OpportunitySet["status"]>,
  current: readonly OpportunitySet[],
): StatusTransition[] {
  const transitions: StatusTransition[] = [];
  for (const set of current) {
    const before = prev.get(set.id);
    if (!before) continue;
    if (before === set.status) continue;
    transitions.push({
      setId: set.id,
      name: set.name,
      from: before,
      to: set.status,
      generationError: set.generationError,
      totalCount: set.totalCount,
    });
  }
  return transitions;
}

/** Builds the next status map for storage in a ref between renders. */
export function buildStatusMap(sets: readonly OpportunitySet[]): Map<string, OpportunitySet["status"]> {
  const m = new Map<string, OpportunitySet["status"]>();
  for (const s of sets) m.set(s.id, s.status);
  return m;
}

/**
 * Row shape returned by the API on create/retry — the subset of `OpportunitySet`
 * that the backend produces without a count aggregation join. The FE fills in
 * count zeros + nulls at the query-cache boundary (see the helpers below).
 */
export interface OpportunitySetRowShape {
  id: string;
  name: string;
  tenantId: string;
  createdById: string | null;
  /** Server enriches create/retry responses with the creator's displayName. */
  createdByName: string | null;
  industry: string | null;
  benchmarkIndustry: string | null;
  myBrand: string | null;
  mainCompetitor: string | null;
  industryCompetitors: string | null;
  products: string[] | null;
  adPlatforms: string[] | null;
  status: OpportunitySet["status"];
  generationStartedAt: string | null;
  generationCompletedAt: string | null;
  generationError: string | null;
  minimalGeneration: boolean;
  strategicPriorities: StrategicPriorities | null;
  brandVoice: BrandVoice | null;
  scriptGuidelines: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Given the current cached list and a freshly-created row, return the next
 * list with the new row spliced in at the top, any pre-existing row with the
 * same id evicted (tolerant of double-fire from React strict mode), and the
 * count fields zeroed because the DAG has not populated them yet.
 */
export function applyCreateToList(
  prev: readonly OpportunitySet[] | undefined,
  created: OpportunitySetRowShape,
): OpportunitySet[] {
  const filtered = prev ? prev.filter((s) => s.id !== created.id) : [];
  const optimistic: OpportunitySet = {
    ...created,
    totalCount: 0,
    activeCount: 0,
    completedScriptsCount: 0,
    rejectedCount: 0,
  };
  return [optimistic, ...filtered];
}

/**
 * Given the current cached list and the retry response, return the next list
 * with the mutable fields patched in. Counts are zeroed since the engine is
 * about to repopulate them. `createdByName` falls back to the cached value
 * when the retry response omits it (legacy rows).
 */
export function applyRetryToList(
  prev: readonly OpportunitySet[] | undefined,
  updated: RetryOpportunitySetResponse,
): OpportunitySet[] | undefined {
  if (!prev) return prev as undefined;
  return prev.map((s) =>
    s.id === updated.replacedSetId
      ? {
          ...s,
          id: updated.id,
          status: updated.status,
          industry: updated.industry ?? s.industry,
          generationStartedAt: updated.generationStartedAt,
          generationError: updated.generationError,
          createdByName: updated.createdByName ?? s.createdByName,
          totalCount: 0,
          activeCount: 0,
          completedScriptsCount: 0,
          rejectedCount: 0,
        }
      : s,
  );
}
